import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import '../../../assets/css/student/studentDashboard/StudentDashboard.css';
import StudentLayout from '../../../layouts/student/studentlayout/StudentLayout';

const StudentDashboard = () => {
  const navigate = useNavigate();

  // Get current date
  const getCurrentDate = () => {
    const options = { weekday: 'long', month: 'short', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  // Sample data - Replace with real API data
  const studentName = "Ahmed";
  const department = "Computer Science";
  
  const stats = [
    { id: 1, title: 'My Groups', value: '4', icon: 'bi-people-fill', color: '#4A7DC4' },
    { id: 2, title: 'Upcoming Sessions', value: '3', icon: 'bi-calendar-event', color: '#5EC5E5' },
    { id: 3, title: 'Completed Sessions', value: '12', icon: 'bi-check-circle-fill', color: '#52C41A' }
  ];

  const upcomingSessions = [
    {
      id: 1,
      title: 'React Hooks Session',
      time: '4:00 PM – 5:30 PM',
      group: 'React Advanced Group',
      icon: 'bi-book'
    },
    {
      id: 2,
      title: 'Data Structures Practice',
      time: '6:00 PM – 7:00 PM',
      group: 'DS & Algorithms',
      icon: 'bi-code-square'
    }
  ];

  const myGroups = [
    {
      id: 1,
      name: 'Data Structures',
      members: 12,
      role: 'Member',
      active: true
    },
    {
      id: 2,
      name: 'React Advanced Group',
      members: 8,
      role: 'Admin',
      active: true
    },
    {
      id: 3,
      name: 'Machine Learning Basics',
      members: 15,
      role: 'Member',
      active: true
    }
  ];

  const recentActivity = [
    { id: 1, text: 'You joined "Machine Learning Basics"', time: '2 hours ago' },
    { id: 2, text: 'New session scheduled in React Group', time: '5 hours ago' },
    { id: 3, text: 'You uploaded notes in Data Structures', time: '1 day ago' }
  ];

  return (
    <StudentLayout>
    <div className="sd-student-dashboard">
      <Container fluid>
        {/* Welcome Section */}
        <div className="sd-welcome-section">
          <h2 className="sd-welcome-title">
            Welcome back, {studentName} 👋
          </h2>
          <p className="sd-welcome-subtitle">
            {department} • {getCurrentDate()}
          </p>
        </div>

        {/* Stats Cards */}
        <Row className="mb-4">
          {stats.map(stat => (
            <Col key={stat.id} lg={4} md={6} className="mb-3">
              <Card className="sd-stat-card">
                <Card.Body className="d-flex align-items-center">
                  <div 
                    className="sd-stat-icon" 
                    style={{ backgroundColor: `${stat.color}15`, color: stat.color }}
                  >
                    <i className={stat.icon}></i>
                  </div>
                  <div className="sd-stat-info">
                    <h3 className="sd-stat-value">{stat.value}</h3>
                    <p className="sd-stat-label">{stat.title}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row>
          {/* Upcoming Sessions */}
          <Col lg={7} className="mb-4">
            <Card className="sd-dashboard-card">
              <Card.Body>
                <div className="sd-card-header-custom">
                  <h5 className="sd-card-title-custom">
                    <i className="bi bi-calendar-event me-2"></i>
                    Today's Sessions
                  </h5>
                </div>
                
                {upcomingSessions.length > 0 ? (
                  <div className="sd-sessions-list">
                    {upcomingSessions.map(session => (
                      <div key={session.id} className="sd-session-item">
                        <div className="sd-session-icon">
                          <i className={session.icon}></i>
                        </div>
                        <div className="sd-session-details">
                          <h6 className="sd-session-title">{session.title}</h6>
                          <p className="sd-session-time">
                            <i className="bi bi-clock me-1"></i>
                            {session.time}
                          </p>
                          <p className="sd-session-group">
                            <i className="bi bi-people me-1"></i>
                            {session.group}
                          </p>
                        </div>
                        <Button 
                          variant="primary" 
                          size="sm" 
                          className="sd-join-btn"
                          onClick={() => navigate('/study-calendar')}
                        >
                          Join Session
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="sd-no-data">
                    <i className="bi bi-calendar-check"></i>
                    <p>No sessions scheduled for today 🎉</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* My Study Groups */}
          <Col lg={5} className="mb-4">
            <Card className="sd-dashboard-card">
              <Card.Body>
                <div className="sd-card-header-custom">
                  <h5 className="sd-card-title-custom">
                    <i className="bi bi-people-fill me-2"></i>
                    My Study Groups
                  </h5>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="sd-view-all-btn"
                    onClick={() => navigate('/student/mystudygroups')}
                  >
                    View All
                  </Button>
                </div>

                <div className="sd-groups-list">
                  {myGroups.map(group => (
                    <div 
                      key={group.id} 
                      className="sd-group-item"
                      onClick={() => navigate('/student/group/' + group.id)}
                    >
                      <div className="sd-group-icon">
                        <i className="bi bi-book"></i>
                      </div>
                      <div className="sd-group-details">
                        <h6 className="sd-group-name">{group.name}</h6>
                        <div className="sd-group-meta">
                          {group.active && (
                            <Badge bg="success" className="me-2">Active</Badge>
                          )}
                          <span className="sd-group-members">
                            <i className="bi bi-people me-1"></i>
                            {group.members} members
                          </span>
                        </div>
                      </div>
                      <Badge bg="info" className="sd-role-badge">
                        {group.role}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>

            {/* Quick Actions */}
            
          </Col>
        </Row>
      </Container>
    </div>
    </StudentLayout>
  );
};

export default StudentDashboard;